import React from 'react';
import './featureTable.css';
import { getIpaDiacriticFeature, ipaToFeatures } from '../../phonemes/ipa';
import { PhonemeItem, PhonemeList } from '../../phonemes/segmentPhonemes';

interface FeaturesForIpaProps {
  readonly segments: PhonemeList;
}

/** display a table breaking down info about all the IPA & its features */
export function FeaturesForIpa(props: FeaturesForIpaProps) {
  return (
    <table>
      <thead>
        <tr>
          <td></td>
          <td></td>
          <th>unicode</th>
          <th>features</th>
        </tr>
      </thead>
      <tbody>
        {props.segments.phonemes.map((s, i) => <SegmentRows phoneme={s} column={0} key={i} />)}
      </tbody>
    </table>
  );
}

interface SegmentRowsProps {
  readonly phoneme: PhonemeItem;
  readonly column: 0 | 1;
  /** in case this is a diacritic, track whether it belongs to a vowel or consonant so we get the feature right */
  readonly parentCat?: 'V' | 'C' | 'O';
}

/** display properties for a segment, breaking it into multiple rows if multiple symbols */
function SegmentRows(props: SegmentRowsProps) {
  const phone = props.phoneme.phoneme;
  const features = props.phoneme.meta === 'unknown' ? ['?']
    : getIpaDiacriticFeature(phone, props.parentCat) ? getIpaDiacriticFeature(phone, props.parentCat)
      : ipaToFeatures(phone)?.join(' ');
  if (features === undefined) {
    return <></>;
  }

  // row for segment
  const row = (
    <tr>
      <th>{props.column === 0 ? props.phoneme.phoneme : ''}</th>
      <th>{props.column === 1 ? props.phoneme.phoneme : ''}</th>
      <td>{getUnicodeString(phone)}</td>
      <td className="features">{features}</td>
    </tr>
  );
  if (phone.length === 1 || features === 'length'
    || asArray(phone).length === 1 // for unicode > 0xffff, e.g., 𝼆
  ) {
    return row;
  }

  // if segment contains multiple symbols, make a row for each symbol
  const parentCat = features.includes('vowel') ? 'V' : features.includes('consonant') ? 'C' : 'O';
  const symbols = asArray(phone);
  const others = (<>
    {symbols.map((c, i) => <SegmentRows phoneme={{ phoneme: c }} column={1} parentCat={parentCat} key={i} />)}
  </>);

  return <>{row}{others}</>;
}

/** get the unicode for a single char, padded to (at least) 4 chars */
export function getUnicodeString(c: string): string {
  switch (c.length) {
    case 1:
      return getPadded(c.charCodeAt(0));
    case 2: // e.g., 𝼆 = 1DF06
      return '1' + getPadded(c.charCodeAt(1));
    default: return '';
  }
}

function getPadded(charCode: number): string {
  const unicode = charCode.toString(16);
  return unicode.length === 4 ? unicode
    : unicode.length === 3 ? '0' + unicode
      : '00' + unicode;
}

/** return padded unicode for every char in string */
export function getUnicodeStringMulti(s: string): string {
  const array = asArray(s);
  const all = array.map(c => getUnicodeString(c));
  return all.join(', ');
}

function asArray(s: string): string[] {
  const array: string[] = [];
  for (const c of s) {
    array.push(c);
  }
  return array;
}
